import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell, Menu, ChevronDown, User, CreditCard, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../features/auth/hooks/useAuth";
import NotificationPanel from "../ui/NotificationPanel";
import { useAnalysis } from "../../features/dashboard/store/analysisContext";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../features/dashboard/services/notificationService";

// How often to poll the unread badge. The backend creates a notification when an
// analysis completes (success or failure), so polling this is how a handed-off /
// background run surfaces on the dashboard without relying on the original
// (possibly lost) synchronous response.
const POLL_MS = 45000;

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(Math.floor((Date.now() - then) / 1000), 0);
  if (secs < 45) return "Just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const Topbar = ({ onToggleSidebar }) => {
  const { state, logout } = useAuth();
  const { state: ctxState } = useAnalysis();
  const navigate = useNavigate();
  const user = state.user || {};

  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Close the account menu on outside click.
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    window.location.href = "/auth/login";
  };

  // Re-fetch when any analysis transitions state, so a completion notification
  // shows up promptly rather than waiting for a full poll interval.
  const statusSignature = Object.values(ctxState.analyses)
    .map((a) => `${a.id}:${a.status}`)
    .join(",");

  const refreshList = useCallback(async () => {
    try {
      const res = await getNotifications({ limit: 20 });
      const data = res?.data ?? {};
      setRows(Array.isArray(data.notifications) ? data.notifications : []);
      if (typeof data.unread_count === "number") setUnreadCount(data.unread_count);
    } catch {
      /* silent — notifications are non-critical chrome */
    }
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      const c = res?.data?.unread_count;
      if (typeof c === "number") setUnreadCount(c);
    } catch {
      /* silent */
    }
  }, []);

  // Initial load + poll the badge.
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Prompt a refresh shortly after an analysis finishes / hands off.
  const sigRef = useRef(statusSignature);
  useEffect(() => {
    if (statusSignature !== sigRef.current) {
      sigRef.current = statusSignature;
      const t = setTimeout(refreshCount, 2500);
      return () => clearTimeout(t);
    }
  }, [statusSignature, refreshCount]);

  // Refresh the full list whenever the panel is opened.
  useEffect(() => {
    if (notifOpen) refreshList();
  }, [notifOpen, refreshList]);

  const handleItemClick = useCallback(
    async (n) => {
      setNotifOpen(false);
      if (!n.read_at) {
        setUnreadCount((c) => Math.max(c - 1, 0));
        try {
          await markNotificationRead(n.id);
        } catch {
          /* ignore — badge already optimistically updated */
        }
      }
      if (n.link) navigate(n.link);
    },
    [navigate],
  );

  const handleMarkAllRead = useCallback(async () => {
    setUnreadCount(0);
    setRows((prev) => prev.map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
    try {
      await markAllNotificationsRead();
    } catch {
      /* ignore */
    }
  }, []);

  const items = rows.map((n) => ({
    id: n.id,
    text: n.subject,
    subtext: n.body,
    time: relativeTime(n.created_at),
    unread: !n.read_at,
    onClick: () => handleItemClick(n),
  }));

  const initials = (user.name || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const displayName = user.name || "User";

  return (
    <>
      <header className="h-16 bg-white border-b border-surface-border flex items-center justify-between px-4 sm:px-6 gap-4 flex-shrink-0">
        {/* Hamburger — mobile only */}
        <button
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg text-brand-muted hover:bg-surface transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bell */}
        <button
          onClick={() => setNotifOpen((o) => !o)}
          className="relative w-9 h-9 rounded-full flex items-center justify-center text-brand-muted hover:bg-surface transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-danger text-white text-[10px] font-bold leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg px-1 py-0.5 hover:bg-surface transition-colors"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold select-none flex-shrink-0">
              {initials}
            </div>
            <span className="hidden sm:block text-sm font-medium text-brand-text max-w-[160px] truncate" title={displayName}>
              {displayName}
            </span>
            <ChevronDown className={`hidden sm:block w-4 h-4 text-brand-muted transition-transform ${menuOpen ? "rotate-180" : ""}`} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-surface-border rounded-xl shadow-xl py-1.5 z-50" role="menu">
              {/* Identity header */}
              <div className="px-4 py-2 border-b border-surface-border mb-1">
                <p className="text-sm font-semibold text-brand-text truncate">{displayName}</p>
                {user.email && <p className="text-xs text-brand-muted truncate">{user.email}</p>}
              </div>
              <button
                onClick={() => { setMenuOpen(false); navigate("/profile"); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-brand-muted hover:bg-surface hover:text-brand-text transition-colors"
                role="menuitem"
              >
                <User className="w-4 h-4 flex-shrink-0" /> Profile
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate("/subscription"); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-brand-muted hover:bg-surface hover:text-brand-text transition-colors"
                role="menuitem"
              >
                <CreditCard className="w-4 h-4 flex-shrink-0" /> My Subscription
              </button>
              <div className="my-1 border-t border-surface-border" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-danger hover:bg-danger/5 transition-colors"
                role="menuitem"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" /> Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {notifOpen && (
        <NotificationPanel
          notifications={items}
          unreadCount={unreadCount}
          onMarkAllRead={handleMarkAllRead}
          onClose={() => setNotifOpen(false)}
        />
      )}
    </>
  );
};

export default Topbar;
